import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  FileUp,
  Send,
  Loader2,
  ClipboardCheck,
  Plus,
  Pencil,
  Trash2,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import { api } from "@/lib/api";
import { useTelecomAccess } from "@/hooks/useTelecomAccess";
import { SectorNoHabilitadoPanel } from "@/components/SectorNoHabilitadoPanel";
import {
  CapturaDocumentos,
  subirDocumentosCapturados,
  type DocumentoCapturado,
} from "@/components/CapturaDocumentos";
import {
  Alert,
  Button,
  Card,
  DataField,
  FormField,
  PageContainer,
  PageHeader,
  Textarea,
} from "@/components/ui";
import {
  NormaModal,
  nuevaNormaForm,
  normaFormToPayload,
  type Norma,
  type NormaForm,
} from "@/components/telecom/NormaModal";

interface CertificadoDetalle {
  id: string;
  numCertificado: string;
  fechaEmision: string;
  titularRazonSocial: string | null;
  productoNombre: string;
  productoModelo: string;
  marca: string;
}

const CONTEXTO_TIPO = "SOLICITUD_TELECOM";
const TAG_DOCS = "TELECOM_REEXP";

/**
 * Pantalla dedicada de Reexpedición de Certificado (Pruebas Complementarias)
 * (#13), mockup 28-Reexpedicion_Certificado.html. Se llega desde la Selección
 * de Certificado con ?cert=<certificadoId>. El titular y los datos de producto
 * vienen del certificado de origen.
 *
 * La carga de documentos de soporte forma parte de la captura: los archivos se
 * seleccionan en el formulario (en memoria) y se suben al contexto de la
 * solicitud inmediatamente después de crearla, antes de confirmar el envío.
 */
export function ReexpedicionAltaPage() {
  const access = useTelecomAccess();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const certId = params.get("cert") ?? "";

  const [normas, setNormas] = useState<NormaForm[]>([]);
  const [modalNorma, setModalNorma] = useState<NormaForm | null>(null);
  const [resumen, setResumen] = useState("");
  const [docs, setDocs] = useState<DocumentoCapturado[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Envío en curso + solicitud ya creada (para reintento de subida sin re-crear).
  const [enviando, setEnviando] = useState(false);
  const [solicitudCreada, setSolicitudCreada] = useState<string | null>(null);

  const qCert = useQuery<CertificadoDetalle>({
    queryKey: ["telecom-certificado", certId],
    queryFn: async () =>
      (await api.get<CertificadoDetalle>(`/api/sector/telecom/certificados/${certId}`)).data,
    enabled: access.allowed && !!certId,
  });

  // Normas ya incluidas en el alcance del certificado (solo-lectura + dedup).
  const qCertNormas = useQuery<Norma[]>({
    queryKey: ["telecom-certificado-normas", certId],
    queryFn: async () =>
      (await api.get<Norma[]>(`/api/sector/telecom/certificados/${certId}/normas`)).data,
    enabled: access.allowed && !!certId,
  });

  // Catálogo completo de normas TELECOM (el modal filtra las ya usadas).
  const qNormasCatalogo = useQuery<Norma[]>({
    queryKey: ["telecom-normas"],
    queryFn: async () => (await api.get<Norma[]>("/api/sector/telecom/normas")).data,
    enabled: access.allowed,
  });

  const construirBody = () => ({
    certificadoBaseId: certId,
    normas: normas.map(normaFormToPayload),
    resumenHallazgos: resumen.trim() || null,
  });

  const enviar = async () => {
    if (normas.length === 0) {
      setError("Agrega al menos una norma pendiente para reexpedir.");
      return;
    }
    setError(null);
    setEnviando(true);
    try {
      let id = solicitudCreada;
      if (!id) {
        id = (
          await api.post<{ id: string }>("/api/sector/telecom/solicitudes/reexpedicion", construirBody())
        ).data.id;
        setSolicitudCreada(id);
      }
      const fallidos = docs.length
        ? await subirDocumentosCapturados(docs, setDocs, CONTEXTO_TIPO, id)
        : 0;
      if (fallidos > 0) {
        setError(
          `La solicitud se registró, pero ${fallidos} documento(s) no se pudieron subir. ` +
            `Reintenta los marcados en rojo o continúa y adjúntalos desde el detalle de la solicitud.`,
        );
        setEnviando(false);
        return;
      }
      navigate(`/sector/telecom/solicitudes/${id}/success`);
    } catch (e: any) {
      setError(
        e?.response?.data?.detail ?? e?.response?.data?.message ?? "No se pudo registrar la solicitud.",
      );
      setEnviando(false);
    }
  };

  if (access.loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-fg-subtle">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando permisos…
      </div>
    );
  }
  if (!access.allowed) {
    return (
      <SectorNoHabilitadoPanel
        sectorNombre="Telecomunicaciones"
        sectorCodigo="TELECOM"
        titularRazonSocial={access.titularRazonSocial}
        sinContexto={access.sinContexto}
      />
    );
  }
  if (!certId) {
    return (
      <Alert variant="warning">
        Falta el certificado. Vuelve a la{" "}
        <Link to="/telecom/servicios/seleccionar-certificado?s=reexpedicion" className="font-medium underline">
          selección de certificado
        </Link>.
      </Alert>
    );
  }

  const bloqueado = !!solicitudCreada;

  // Normas vetadas para agregar: las del alcance del certificado + las ya
  // agregadas (salvo la que se esté editando, que el modal reincorpora).
  const idsCertificado = (qCertNormas.data ?? []).map((n) => n.id);
  const yaUsadas = [
    ...idsCertificado,
    ...normas.filter((n) => n.uiId !== modalNorma?.uiId).map((n) => n.normaId),
  ];

  const normaValida = normas.length > 0;

  return (
    <PageContainer width="form">
      <PageHeader
        backTo="/telecom/servicios/seleccionar-certificado?s=reexpedicion"
        backLabel="Cambiar certificado"
        icon={<FileUp className="h-5 w-5" />}
        title="Reexpedición de Certificado"
        subtitle="Incorpora una o más NOM/DT adicionales al alcance del certificado vigente por pruebas complementarias."
      />

      <Alert variant="info">
        Solo puedes agregar normas que aún no formen parte del certificado.
      </Alert>

      {/* Certificado seleccionado */}
      <Card title="Certificado seleccionado">
        {qCert.isLoading ? (
          <div className="text-sm text-fg-subtle">Cargando certificado…</div>
        ) : qCert.error || !qCert.data ? (
          <div className="text-sm text-estado-warn">No se pudo cargar el certificado seleccionado.</div>
        ) : (
          <dl className="grid gap-4 sm:grid-cols-2">
            <DataField label="Núm. de Certificado" value={<span className="font-mono">{qCert.data.numCertificado}</span>} />
            <DataField label="Razón Social" value={qCert.data.titularRazonSocial ?? "—"} />
            <DataField label="Producto" value={`${qCert.data.productoNombre} ${qCert.data.productoModelo}`} />
            <DataField label="Fecha de expedición" value={formatDate(qCert.data.fechaEmision)} />
          </dl>
        )}
      </Card>

      {/* Motivo: tarjeta fija Pruebas Complementarias */}
      <Card title="Motivo de la reexpedición">
        <div className="flex items-start gap-3 rounded-lg border border-primary-strong/40 bg-primary-tint p-4">
          <ClipboardCheck className="mt-0.5 h-6 w-6 shrink-0 text-primary-strong" />
          <div>
            <div className="text-sm font-semibold text-primary-strong">Pruebas complementarias</div>
            <p className="mt-0.5 text-xs text-fg-muted">
              El producto fue sometido a pruebas adicionales (por ejemplo nuevas NOM aplicables) que
              ameritan reexpedición. El cambio de filiales/subsidiarias tiene su propia pantalla.
            </p>
          </div>
        </div>
      </Card>

      {/* Normas ya certificadas (solo-lectura) */}
      <Card title="Normas ya incluidas en el certificado">
        {qCertNormas.isLoading ? (
          <div className="text-sm text-fg-subtle">Cargando alcance del certificado…</div>
        ) : (qCertNormas.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-fg-subtle">El certificado no tiene normas registradas en su alcance.</p>
        ) : (
          <ul className="space-y-1">
            {qCertNormas.data!.map((n) => (
              <li key={n.id} className="flex items-center gap-2 rounded-lg bg-surface-2 px-3 py-2 text-sm text-fg-muted">
                <ShieldCheck className="h-4 w-4 shrink-0 text-estado-ok" />
                <span className="font-mono text-xs">{n.codigo}</span>
                <span className="truncate text-xs text-fg-subtle">— {n.titulo}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* NOM/DT adicionales a incorporar */}
      <Card
        title={`NOM / DT adicionales a incorporar${normas.length > 0 ? ` (${normas.length})` : ""}`}
        actions={
          !bloqueado && (
            <Button
              size="sm"
              variant="secondary"
              icon={<Plus className="h-3.5 w-3.5" />}
              onClick={() => setModalNorma(nuevaNormaForm({ modelosEvaluados: qCert.data?.productoModelo }))}
              disabled={qNormasCatalogo.isLoading || qCertNormas.isLoading}
            >
              Agregar norma
            </Button>
          )
        }
      >
        {normas.length === 0 ? (
          <div className="rounded-lg border border-dashed border-edge-strong p-3 text-sm text-fg-subtle">
            Aún no agregas normas pendientes. Agrega al menos una para reexpedir el certificado.
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-xs uppercase text-fg-subtle">
              <tr>
                <th className="px-3 py-2.5 text-left">Norma</th>
                <th className="px-3 py-2.5 text-left">Laboratorio</th>
                <th className="px-3 py-2.5 text-left">Fecha de prueba</th>
                {!bloqueado && <th className="w-24 px-3 py-2.5"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-edge">
              {normas.map((n, i) => (
                <tr key={n.uiId}>
                  <td className="px-3 py-2.5 font-mono text-xs">{n.normaCodigo}</td>
                  <td className="px-3 py-2.5 text-xs">{n.esNom221P1 ? `${n.numImeis ?? 0} IMEIs` : (n.laboratorioNombre || "—")}</td>
                  <td className="px-3 py-2.5 text-xs">{n.fechaEmision || "—"}</td>
                  {!bloqueado && (
                    <td className="px-3 py-2.5 text-right">
                      <button type="button" onClick={() => setModalNorma(n)} className="rounded p-1 text-fg-muted transition hover:bg-surface-2"><Pencil className="h-4 w-4" /></button>
                      <button type="button" onClick={() => setNormas((arr) => arr.filter((_, idx) => idx !== i))} className="rounded p-1 text-estado-err transition hover:bg-estado-err-bg"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        <FormField label="Resumen / hallazgos de la prueba" className="mt-4">
          <Textarea
            value={resumen}
            onChange={(e) => setResumen(e.target.value)}
            disabled={bloqueado}
            rows={3}
            placeholder="Describe los resultados de las pruebas complementarias (opcional)."
          />
        </FormField>
      </Card>

      {/* Documentos de soporte — parte de la captura */}
      <CapturaDocumentos
        tag={TAG_DOCS}
        titulo="Documentos de soporte"
        ayuda="Adjunta los documentos del trámite. Se subirán junto con la solicitud al enviarla."
        docs={docs}
        onChange={setDocs}
        disabled={enviando}
      />

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Link to="/telecom/servicios/seleccionar-certificado?s=reexpedicion">
          <Button variant="secondary" className="w-full sm:w-auto">Regresar</Button>
        </Link>
        {bloqueado ? (
          <>
            <Button
              variant="secondary"
              loading={enviando}
              onClick={enviar}
              className="w-full sm:w-auto"
            >
              Reintentar documentos
            </Button>
            <Button
              variant="success"
              icon={<CheckCircle2 className="h-4 w-4" />}
              onClick={() => navigate(`/sector/telecom/solicitudes/${solicitudCreada}/success`)}
              className="w-full sm:w-auto"
            >
              Continuar
            </Button>
          </>
        ) : (
          <Button
            variant="success"
            icon={<Send className="h-4 w-4" />}
            loading={enviando}
            disabled={!normaValida || qCert.isLoading || !!qCert.error}
            onClick={enviar}
            className="w-full sm:w-auto"
          >
            Enviar solicitud
          </Button>
        )}
      </div>

      {modalNorma && (
        <NormaModal
          initial={modalNorma}
          normas={qNormasCatalogo.data ?? []}
          yaUsadas={yaUsadas}
          onCancel={() => setModalNorma(null)}
          onSave={(n) => {
            setNormas((arr) => {
              const i = arr.findIndex((x) => x.uiId === n.uiId);
              if (i >= 0) { const next = [...arr]; next[i] = n; return next; }
              return [...arr, n];
            });
            setModalNorma(null);
          }}
        />
      )}
    </PageContainer>
  );
}

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return s;
  }
}
